import { configuredLanguage, normalizeTutorialLanguage } from "../tutorials/language";
import { terms } from "./terms";

export const siteContent = {
  en: {
    direction: "ltr",
    common: {
      brandName: "Pocket Play",
      games: "Games",
      about: "About",
      contact: "Contact us",
      plans: "Plans",
      terms: "Terms of Service",
      openOn: "Open {{platform}}",
      comingSoon: "Link coming soon",
    },
    about: {
      remote: {
        title: "About Pocket Play",
        subtitle: "How Pocket Play works",
        sectionTitle: "Sections",
        website: "Website usage",
        play: "How to play",
        motion: "Motion games",
        tournaments: "Tournaments",
        games: "Browse games",
        plans: "View plans",
      },
      hero: {
        eyebrow: "About",
        title: "How Pocket Play Works",
        description: "Play games on a large screen and use phones as controllers, including motion-based games built for active multiplayer.",
      },
      website: {
        eyebrow: "Website usage",
        title: "Welcome to Our Game Website",
        paragraphs: [
          "Everything you need to play is available directly inside this website. Simply log in to your account, choose a game, and start playing.",
          "You can open the website on any device, but remember that the game runs on that device's screen. Since everyone needs to see the game clearly, we recommend using a device with a large screen, or connecting or mirroring your device to a TV or bigger display for the best experience.",
          "For easier control from across the room, use the Phone Remote feature. Select Phone Remote in the website header, scan the QR code with your phone, and your phone will become a context-aware remote. Its controls automatically change depending on the page, allowing you to navigate the website, search and filter games, select a game, change pages, control music, and more.",
        ],
        imageAlt: "Using a phone as a context-aware remote for Pocket Play on a large screen",
      },
      play: {
        eyebrow: "Website usage",
        title: "How to Play?",
        paragraphs: [
          "After you choose a game to play, a QR code will appear on the screen. Players can scan this QR code with their phones to join the game and use their devices as controllers.",
          "Once all players have joined and are ready, press Start to begin the game.",
          "You can press Start using the device's usual controls, such as a mouse on a laptop or a TV remote. For an easier option, use your phone: after scanning the QR code and joining the game, tap the Settings icon at the top of the controller page to start, pause or resume the game, and control the music.",
          "Before choosing another game, please close the current game tab to make sure everything runs smoothly.",
        ],
        steps: ["Choose a game", "Scan the QR code", "Players join", "Press Start"],
        imageAlt: "How to use Pocket Play and join games with a phone",
      },
      motion: {
        eyebrow: "Active play",
        title: "Motion Games",
        paragraphs: [
          "Some games on the website are marked as Motion Games. These games use real physical movement as the input of the game.",
          "For example, in Running Race, your phone's camera can detect actions like walking, running, and jumping. It works like using your phone as a motion-tracking controller, similar to a Kinect-style experience.",
          "Motion Games also include a special multiplayer option. In regular games, each player usually needs their own phone as a controller. But in Motion Games, one phone can detect up to 4 players at the same time. This means, for example, 8 players can play using only 2 phones.",
          "Motion Games are a fun way to make gameplay more active, social, and exciting.",
        ],
        highlights: ["Camera motion tracking", "Up to 4 players per phone", "Active multiplayer"],
        imageAlt: "Motion games using phone camera movement tracking",
      },
      tournaments: {
        eyebrow: "Ultimate competition",
        title: "Tournaments",
        description: "Turn any game night into an epic championship! Create custom tournaments, choose your games and rounds, and compete individually or in teams through cumulative scoring or exciting elimination modes. Players join once with a QR code, use their phones as controllers, and stay connected as the tournament moves seamlessly from game to game—with live standings, reconnection support, and motion-based camera games for even more fun. Gather your friends, raise the stakes, and crown the ultimate champion!",
        imageAlt: "Players competing in a custom Pocket Play tournament",
      },
      callToAction: {
        title: "Ready to play?",
        description: "Browse the game library and start a session from the main page.",
        games: "Browse Games",
        plans: "View Plans",
      },
    },
    footer: {
      description: "Big-screen games powered by the phones already in your hands.",
      explore: "Explore",
      connect: "Connect",
      rights: "All rights reserved.",
      unavailable: "This social link will be available soon.",
    },
    contact: {
      remote: {
        title: "Contact Pocket Play",
        subtitle: "Find us on social media",
        sectionTitle: "Social channels",
      },
      eyebrow: "Let's talk",
      title: "Contact us",
      description: "Questions, feedback, or an idea for a game night? Choose the social channel that works best for you and send us a message.",
      availability: "Our social profiles are the quickest way to reach the Pocket Play team. We will get back to you as soon as we can.",
      channels: {
        telegram: {
          label: "Telegram",
          description: "Follow announcements and send us quick questions through Telegram.",
        },
        whatsapp: {
          label: "WhatsApp",
          description: "Use WhatsApp for direct support and questions about getting started.",
        },
        instagram: {
          label: "Instagram",
          description: "See Pocket Play in action, discover new games, and message us on Instagram.",
        },
      },
    },
    terms: terms.en,
  },
  fa: {
    direction: "rtl",
    common: {
      brandName: "پاکت پلی",
      games: "بازی‌ها",
      about: "درباره",
      contact: "تماس با ما",
      plans: "پلن‌ها",
      terms: "قوانین و مقررات",
      openOn: "مشاهده در {{platform}}",
      comingSoon: "لینک به‌زودی اضافه می‌شود",
    },
    about: {
      remote: {
        title: "درباره پاکت پلی",
        subtitle: "پاکت پلی چگونه کار می‌کند",
        sectionTitle: "بخش‌ها",
        website: "استفاده از وب‌سایت",
        play: "روش بازی",
        motion: "بازی‌های حرکتی",
        tournaments: "مسابقات",
        games: "مشاهده بازی‌ها",
        plans: "مشاهده پلن‌ها",
      },
      hero: {
        eyebrow: "درباره",
        title: "پاکت پلی چگونه کار می‌کند؟",
        description: "بازی‌ها را روی یک نمایشگر بزرگ اجرا کنید و از گوشی‌ها به‌عنوان دسته بازی استفاده کنید؛ حتی در بازی‌های حرکتی و چندنفره‌ای که شما را به جنب‌وجوش می‌آورند.",
      },
      website: {
        eyebrow: "استفاده از وب‌سایت",
        title: "به وب‌سایت بازی ما خوش آمدید",
        paragraphs: [
          "هر چیزی که برای بازی نیاز دارید، مستقیماً در همین وب‌سایت در دسترس است. کافی است وارد حساب کاربری خود شوید، یک بازی را انتخاب کنید و بازی را شروع کنید.",
          "می‌توانید وب‌سایت را روی هر دستگاهی باز کنید، اما به یاد داشته باشید که بازی روی نمایشگر همان دستگاه اجرا می‌شود. چون همه بازیکنان باید تصویر بازی را به‌خوبی ببینند، پیشنهاد می‌کنیم از دستگاهی با نمایشگر بزرگ استفاده کنید یا برای بهترین تجربه، تصویر دستگاه را به تلویزیون یا نمایشگری بزرگ‌تر منتقل کنید.",
          "برای کنترل راحت‌تر از فاصله دور، از قابلیت ریموت موبایل استفاده کنید. در سربرگ وب‌سایت گزینه «ریموت موبایل» را انتخاب کنید و کد QR را با گوشی اسکن کنید تا گوشی شما به یک ریموت هوشمند تبدیل شود. کنترل‌های آن متناسب با هر صفحه تغییر می‌کنند و می‌توانید با آن در سایت جابه‌جا شوید، بازی‌ها را جست‌وجو و فیلتر کنید، بازی انتخاب کنید، صفحه را تغییر دهید، موسیقی را کنترل کنید و غیره.",
        ],
        imageAlt: "استفاده از گوشی به‌عنوان ریموت هوشمند پاکت پلی روی نمایشگر بزرگ",
      },
      play: {
        eyebrow: "استفاده از وب‌سایت",
        title: "چطور بازی کنیم؟",
        paragraphs: [
          "پس از انتخاب بازی، یک کد QR روی نمایشگر ظاهر می‌شود. بازیکنان می‌توانند این کد را با گوشی خود اسکن کنند، وارد بازی شوند و از گوشی به‌عنوان دسته بازی استفاده کنند.",
          "وقتی همه بازیکنان وارد شدند و آماده بودند، دکمه «شروع» را بزنید تا بازی آغاز شود.",
          "برای شروع می‌توانید از ابزار معمول همان دستگاه، مانند ماوس لپ‌تاپ یا کنترل تلویزیون، استفاده کنید. راه ساده‌تر استفاده از گوشی است: پس از اسکن کد QR و ورود به بازی، آیکون تنظیمات در بالای صفحه کنترلر را لمس کنید تا بازی را شروع، متوقف یا ادامه دهید و موسیقی را کنترل کنید.",
          "پیش از انتخاب یک بازی دیگر، لطفاً زبانه بازی فعلی را ببندید تا همه‌چیز روان اجرا شود.",
        ],
        steps: ["یک بازی انتخاب کنید", "کد QR را اسکن کنید", "بازیکنان وارد می‌شوند", "شروع را بزنید"],
        imageAlt: "روش استفاده از پاکت پلی و ورود به بازی با گوشی",
      },
      motion: {
        eyebrow: "بازی پرتحرک",
        title: "بازی‌های حرکتی",
        paragraphs: [
          "برخی بازی‌های وب‌سایت با عنوان «بازی حرکتی» مشخص شده‌اند. در این بازی‌ها، حرکت واقعی بدن ورودی بازی است.",
          "برای نمونه، در بازی مسابقه دو، دوربین گوشی می‌تواند حرکت‌هایی مانند راه‌رفتن، دویدن و پریدن را تشخیص دهد. در واقع گوشی شما مانند یک کنترلر رهگیری حرکت و شبیه به تجربه کینکت عمل می‌کند.",
          "بازی‌های حرکتی یک قابلیت چندنفره ویژه هم دارند. در بازی‌های معمولی هر بازیکن اغلب به یک گوشی جداگانه نیاز دارد؛ اما در بازی‌های حرکتی، یک گوشی می‌تواند هم‌زمان حرکت حداکثر ۴ بازیکن را تشخیص دهد. یعنی برای مثال ۸ نفر می‌توانند تنها با ۲ گوشی بازی کنند.",
          "بازی‌های حرکتی راهی سرگرم‌کننده برای ساختن تجربه‌ای پرتحرک‌تر، اجتماعی‌تر و هیجان‌انگیزتر هستند.",
        ],
        highlights: ["رهگیری حرکت با دوربین", "تا ۴ بازیکن با هر گوشی", "بازی چندنفره و پرتحرک"],
        imageAlt: "بازی حرکتی با رهگیری حرکت بازیکنان از طریق دوربین گوشی",
      },
      tournaments: {
        eyebrow: "رقابت نهایی",
        title: "تورنمنت",
        description: "هر دورهمی بازی را به یک رقابت فراموش‌نشدنی تبدیل کنید! تورنمنت دلخواه خود را بسازید، بازی‌ها و راندها را انتخاب کنید و به‌صورت انفرادی یا تیمی، با امتیازدهی تجمعی یا مراحل حذفی هیجان‌انگیز رقابت کنید. بازیکنان فقط یک‌بار با کد QR وارد می‌شوند، از گوشی‌هایشان به‌عنوان دسته بازی استفاده می‌کنند و با جابه‌جایی روان مسابقه از یک بازی به بازی دیگر همچنان متصل می‌مانند. جدول رده‌بندی زنده، اتصال دوباره و بازی‌های حرکتی مبتنی بر دوربین هم هیجان را بیشتر می‌کنند. دوستانتان را جمع کنید، رقابت را جدی‌تر کنید و قهرمان نهایی را مشخص کنید!",
        imageAlt: "رقابت بازیکنان در یک تورنمنت کاستوم پاکت پلی",
      },
      callToAction: {
        title: "آماده بازی هستید؟",
        description: "فهرست بازی‌ها را ببینید و از صفحه اصلی یک بازی را شروع کنید.",
        games: "مشاهده بازی‌ها",
        plans: "مشاهده پلن‌ها",
      },
    },
    footer: {
      description: "بازی‌ روی نمایشگر بزرگ، با گوشی‌هایی که همین حالا در دست دارید.",
      explore: "دسترسی سریع",
      connect: "شبکه‌های اجتماعی",
      rights: "تمامی حقوق محفوظ است.",
      unavailable: "لینک این شبکه اجتماعی به‌زودی اضافه می‌شود.",
    },
    contact: {
      remote: {
        title: "تماس با پاکت پلی",
        subtitle: "ما را در شبکه‌های اجتماعی پیدا کنید",
        sectionTitle: "راه‌های ارتباطی",
      },
      eyebrow: "با هم در ارتباط باشیم",
      title: "تماس با ما",
      description: "سؤال، پیشنهاد یا ایده‌ای برای یک دورهمی بازی دارید؟ شبکه اجتماعی دلخواهتان را انتخاب کنید و برای ما پیام بفرستید.",
      availability: "شبکه‌های اجتماعی سریع‌ترین راه ارتباط با تیم پاکت پلی هستند. تلاش می‌کنیم در اولین فرصت پاسخ شما را بدهیم.",
      details: {
        title: "تماس با PocketPlay",
        description: "برای ارتباط با پشتیبانی PocketPlay و پیگیری درخواست‌های خود می‌توانید از راه‌های زیر استفاده کنید.",
        items: [
          { label: "آدرس", value: "تهران - قیطریه - کیکاووس - معنوی (پانزدهم) - پلاک 15 - واحد 3" },
          { label: "کد پستی", value: "1931716654", ltr: true },
          { label: "ایمیل پشتیبانی", value: "hossein@fatemi.net", href: "mailto:hossein@fatemi.net", ltr: true },
          { label: "شماره تماس", value: "09051151251", href: "tel:09051151251", ltr: true },
        ],
      },
      channels: {
        telegram: {
          label: "تلگرام",
          description: "خبرها و اطلاعیه‌های ما را دنبال کنید و پرسش‌های کوتاه خود را در تلگرام بفرستید.",
        },
        whatsapp: {
          label: "واتساپ",
          description: "برای دریافت پشتیبانی مستقیم و پرسش درباره شروع کار، از واتساپ استفاده کنید.",
        },
        instagram: {
          label: "اینستاگرام",
          description: "پاکت پلی را در حال اجرا ببینید، بازی‌های تازه را کشف کنید و در اینستاگرام برای ما پیام بفرستید.",
        },
      },
    },
    terms: terms.fa,
  },
};

export function getSiteContent(language = configuredLanguage) {
  return siteContent[normalizeTutorialLanguage(language)];
}

export function interpolate(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{{${key}}}`, value),
    template,
  );
}
